import React from 'react'

import { FormattedMessage } from 'react-intl'

import { AmountInput, Typography } from '@components/ui'

import { MaxAmountLabelMessage, MinAmountLabelMessage } from '../messages'
import { FilterWrapper } from '../styles'
import { AdornmentTypography, Divider, ErrorTypography, InputWrapper } from './styles'

interface AmountFiltersProps {
  onChange: (values: AmountFilterProps) => void
  onValidityChange?: (valid: boolean) => void
  values: AmountFilterProps
  validationBehavior?: 'onBlur' | 'onChange'
}

const ERROR_ID = 'amount-validation-error'

function validate(grossAmountMin: Decimal, grossAmountMax: Decimal) {
  return Number(grossAmountMin) <= Number(grossAmountMax)
}

export default function AmountFilters({
  onChange,
  onValidityChange,
  validationBehavior = 'onBlur',
  values,
}: AmountFiltersProps) {
  const [hasError, setError] = React.useState(false)

  const handleMinChange = React.useCallback(
    (inputValue: Decimal) => {
      if (validationBehavior === 'onChange') {
        const isValid =
          values.grossAmountMax != null && inputValue != '' ? validate(inputValue, values.grossAmountMax) : true

        if (hasError !== !isValid) {
          setError(!isValid)
          onValidityChange?.(isValid)
        }
      }
      // use "undefined" when inputValue is empty string - input cleared
      onChange({ ...values, grossAmountMin: inputValue || undefined })
    },
    [validationBehavior, onChange, values, hasError, onValidityChange]
  )

  const handleMaxChange = React.useCallback(
    (inputValue: Decimal) => {
      if (validationBehavior === 'onChange') {
        const isValid =
          values.grossAmountMin != null && inputValue != '' ? validate(values.grossAmountMin, inputValue) : true

        if (hasError !== !isValid) {
          setError(!isValid)
          onValidityChange?.(isValid)
        }
      }
      // use "undefined" when inputValue is empty string - input cleared
      onChange({ ...values, grossAmountMax: inputValue || undefined })
    },
    [validationBehavior, onChange, values, hasError, onValidityChange]
  )

  const handleBlur = React.useCallback(() => {
    const isValid =
      values.grossAmountMax != null && values.grossAmountMin != null
        ? validate(values.grossAmountMin, values.grossAmountMax)
        : true

    if (hasError !== !isValid) {
      setError(!isValid)
      onValidityChange?.(isValid)
    }
  }, [values, hasError, onValidityChange])

  return (
    <FilterWrapper data-testid="amount-filter-wrapper">
      <Typography size="700-xs" color="gray-80" uppercase tag="h4">
        <FormattedMessage id="dialogs.filters.labels.amount" defaultMessage="Összeg" />
      </Typography>
      <InputWrapper>
        <AmountInput
          name="filter-grossAmountMin"
          error={hasError}
          value={values.grossAmountMin ?? ''}
          onChange={handleMinChange}
          endAdornment={
            <AdornmentTypography size="400-xs" color="gray-50" tag="span">
              {MinAmountLabelMessage}
            </AdornmentTypography>
          }
          {...(validationBehavior === 'onBlur' && { onBlur: handleBlur })}
        />
        <Divider />
        <AmountInput
          name="filter-grossAmountMax"
          error={hasError}
          value={values.grossAmountMax ?? ''}
          onChange={handleMaxChange}
          endAdornment={
            <AdornmentTypography size="400-xs" color="gray-50" tag="span">
              {MaxAmountLabelMessage}
            </AdornmentTypography>
          }
          {...(validationBehavior === 'onBlur' && { onBlur: handleBlur })}
        />
      </InputWrapper>
      {hasError && (
        <ErrorTypography id={ERROR_ID} size="400-xs" color="error" data-error>
          <FormattedMessage
            id="dialogs.filters.amount.validationError"
            defaultMessage="A minimum összeg nem lehet nagyobb, mint a maximum összeg"
          />
        </ErrorTypography>
      )}
    </FilterWrapper>
  )
}
